import { useCallback, useEffect, useMemo, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { FileText, Eye, Clock, Hash } from "lucide-react";
import "./style.css";
import { MenuBar } from "./components/menu-bar";
import { ResizableImage } from 'tiptap-extension-resizable-image';

export type DarkBlogEditorProps = {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  className?: string;
  autoSaveKey?: string;
  readOnly?: boolean;
};


// --- Estimador de tempo de leitura
function estimateReadingTime(html: string) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  const text = tmp.textContent || tmp.innerText || "";
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return { words, minutes };
}


export default function TextEditor({
  value,
  onChange,
  placeholder = "Comece a escrever seu artigo...",
  className = "",
  autoSaveKey,
  readOnly = false,
}: DarkBlogEditorProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveStatus, setSaveStatus] = useState<"saved" | "saving" | "unsaved">("saved");
  const [html, setHtml] = useState<string>(value || "");

  // Sincronizar com o value prop
  useEffect(() => {
    if (value === undefined) return;
    setHtml((currentHtml) => (currentHtml !== value ? value : currentHtml));
  }, [value]);

  // AutoLoad
  useEffect(() => {
    if (!autoSaveKey) return;
    const savedContent = localStorage.getItem(`${autoSaveKey}:content`);
    if (savedContent !== null) setHtml(savedContent);
  }, [autoSaveKey]);

  // AutoSave
  useEffect(() => {
    if (!autoSaveKey) return;
    setSaveStatus("saving");

    const id = setTimeout(() => {
      localStorage.setItem(`${autoSaveKey}:content`, html);
      setLastSaved(new Date());
      setSaveStatus("saved");
    }, 1000);

    return () => clearTimeout(id);
  }, [html, autoSaveKey]);

  const handleChange = useCallback((newHtml: string) => {
    setHtml(newHtml);
    setSaveStatus("unsaved");
    onChange?.(newHtml);
  }, [onChange]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        bulletList: {
          HTMLAttributes: {
            class: 'list-disc pl-4',
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal pl-4',
          },
        },
        blockquote: {
          HTMLAttributes: {
            class: 'border-l-4 border-zinc-600 pl-4 py-1 my-2',
          },
        },
        codeBlock: {
          HTMLAttributes: {
            class: 'bg-zinc-800 p-4 rounded my-2',
          },
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-400 hover:underline',
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'rounded max-w-full h-auto',
        },
      }),
      ResizableImage.configure({
        defaultWidth: 200,
        defaultHeight: 200,
      })
    ],
    content: html,
    onUpdate: ({ editor }) => {
      handleChange(editor.getHTML());
    },
    editable: !readOnly,
  });

  // Atualizar o editor quando o html muda
  useEffect(() => {
    if (editor && html !== editor.getHTML()) {
      editor.commands.setContent(html);
    }
  }, [html, editor]);

  const { words, minutes } = useMemo(() => estimateReadingTime(html), [html]);

  // Função para lidar com drag and drop de imagens
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0 && editor) {
      const file = imageFiles[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        const src = e.target?.result as string;
        editor.chain().focus().setImage({ src }).run();
      };
      reader.readAsDataURL(file);
    }
  }, [editor]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  if (!editor) {
    return <div>Carregando editor...</div>;
  }

  return (
    <div className={`${isFullscreen ? "fixed inset-0 z-50 bg-[#0D0D0D]" : "mx-auto max-w-5xl"} ${className}`}>
      <div className="bg-[#1A1A1A] ">
        {/* Header */}
        <div className="border-b border-zinc-800 bg-zinc-900/70 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-blue-400" />
              <span className="text-sm font-medium text-zinc-300">Editor de Texto</span>
            </div>

            <div className="flex items-center space-x-3">
              {saveStatus === "saving" && (
                <span className="text-xs text-zinc-400">Salvando...</span>
              )}
              {saveStatus === "saved" && lastSaved && (
                <span className="text-xs text-zinc-400">
                  Salvo {lastSaved.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                title={isFullscreen ? "Sair do modo tela cheia" : "Modo tela cheia"}
              >
                <Eye className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="max-w-4xl mx-auto ">
          {!readOnly && <MenuBar editor={editor} />}
          <div className="prose dark:prose-invert prose-zinc max-w-none prose-headings:text-zinc-200 prose-p:text-zinc-300 prose-strong:text-white prose-a:text-blue-400 prose-blockquote:text-zinc-400 prose-ul:list-disc prose-ol:list-decimal prose-li:marker:text-zinc-400">
            <EditorContent 
              editor={editor} 
              className="min-h-[300px] p-4 focus:outline-none" 
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            />
          </div>

          {/* Stats Footer */}
          <div className="mt-6 flex items-center justify-between text-sm">
            <div className="flex items-center space-x-6 text-zinc-400">
              <div className="flex items-center space-x-2">
                <Hash className="w-4 h-4" />
                <span>
                  <span className="font-medium text-zinc-200">{words.toLocaleString()}</span> palavras
                </span>
              </div>

              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span>
                  <span className="font-medium text-zinc-200">{minutes}</span> min de leitura
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
